/**
 * Cooking a pot: what went in, then how it is divided.
 *
 * The page that owns the draft. `PotBuilder` and `PotDivider` are two views of
 * one pot (`components/pot.ts`), and they are the same components the add sheet
 * used to render as modes of itself. They moved here for the reason the sheet's
 * own header gave for breaking its one rule on their behalf: a pot is a
 * statement about a pan rather than about a day, it never wrote instantly, and
 * it needed a header the sheet could not give it. A route gives it one, and
 * gives the draft a life that does not end when somebody presses Escape.
 *
 * One route for both steps rather than `/prepare/new/divide`, because the draft
 * is component state and dies on refresh — a deep link to the divider would
 * open on an empty pot. The divider is a step of the flow exactly as search,
 * scan and weigh are steps of the builder.
 *
 * Storing lands on the pot's own page. "Once prepared you have the option to
 * share it" is one tap from there and none from here.
 */

import { useCookPot, useRecipes } from "@calorio/core";
import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { PotBuilder, PotDivider } from "../components/PotFlow";
import { potInput, usePotDraft } from "../components/pot";
import { closeCamera } from "../lib/camera";
import { useKeyboard } from "../lib/keyboard";

export function PrepareFlow() {
  const ctx = useCalorio();
  const navigate = useNavigate();
  const pot = usePotDraft();
  const recipes = useRecipes(ctx);
  const cookPot = useCookPot(ctx);
  const [step, setStep] = useState<"build" | "divide">("build");
  const keyboard = useKeyboard();

  /* Leaving the page is the end of scanning, the way closing the sheet was.
     `ScanPane` holds the camera across its own remounts on purpose — see
     `lib/camera.ts` — so somebody has to say when it is over, and on a route
     that somebody is the route. */
  useEffect(() => closeCamera, []);

  /**
   * Take the pot to the server, once, with everything on it decided — unlike
   * every other gesture in the app, and for the reason in the header: nothing
   * here has been logged, so there is nothing to lose by not having written
   * it yet.
   */
  const store = () => {
    const { draft, figures } = pot;
    if (figures.empty) return;
    cookPot
      .mutateAsync(potInput(draft, figures))
      .then((batch) => {
        pot.actions.reset();
        navigate(`/prepare/${batch.id}`, { replace: true });
      })
      .catch(() => undefined);
  };

  return (
    <div
      className="prepare-flow"
      /* The column is bounded to the screen so the docked field never scrolls
         away, and on iOS the screen is not what the layout viewport says it is
         once the keyboard is up: the field would dock behind it. Measured
         rather than assumed — see `lib/keyboard.ts`, which the add sheet has
         used for the same reason since it had a field of its own. */
      style={{ "--keyboard-inset": `${keyboard.inset}px` } as CSSProperties}
    >
      <div className="editor__back">
        <button className="editor__back-link" onClick={() => navigate("/prepare")}>
          ‹ Prepare
        </button>
        {step === "divide" && (
          <button className="sheet__action" onClick={() => setStep("build")}>
            Back to the pot
          </button>
        )}
      </div>
      <h1 className="editor__title">{step === "build" ? "New pot" : "Divide a pot"}</h1>

      {step === "build" ? (
        <PotBuilder
          draft={pot.draft}
          figures={pot.figures}
          actions={pot.actions}
          recipes={recipes.data}
          onDivide={() => setStep("divide")}
        />
      ) : (
        <PotDivider
          draft={pot.draft}
          figures={pot.figures}
          actions={pot.actions}
          storing={cookPot.isPending}
          onBack={() => setStep("build")}
          onStore={store}
        />
      )}
    </div>
  );
}
